import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { AttachmentUpload } from 'components/FormFields';

// Modal overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Modal container
const ModalContainer = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1F2937' : '#ffffff'};
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  box-shadow: ${({ theme }) =>
    theme?.name === 'dark'
      ? '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
  animation: slideIn 0.3s ease-out;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// Modal header
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#F3F4F6' : '#0F172A')};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#374151' : '#f3f4f6'};
  border: none;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#9CA3AF' : '#6b7280')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark' ? '#4B5563' : '#e5e7eb'};
    color: ${({ theme }) => (theme?.name === 'dark' ? '#F3F4F6' : '#374151')};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Modal body
const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

// Form elements
const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#e2e8f0' : '#374151')};
  margin-bottom: 8px;
`;

const RequiredBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #ef4444;
  margin-left: 6px;
`;

const OptionalBadge = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#64748b' : '#94a3b8')};
  margin-left: 6px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme, error }) =>
      error
        ? '#ef4444'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => (theme?.name === 'dark' ? '#64748b' : '#94a3b8')};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  transition: all 0.2s;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => (theme?.name === 'dark' ? '#64748b' : '#94a3b8')};
  }
`;

const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 8px 0 0 0;
  line-height: 1.5;
`;

// Wrapper to constrain AttachmentUpload size for single image
const AttachmentWrapper = styled.div`
  & > div > div:last-child > div {
    max-width: 120px;
  }
`;

// Modal footer
const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#374151' : '#e2e8f0')};
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#111827' : '#f9fafb'};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#9CA3AF' : '#64748b')};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #fa4616, #ff6b3b);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Icons
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DivisionModal = ({ isOpen, onClose, onSubmit, division, loading }) => {
  const isEditMode = !!division;
  const [divisionName, setDivisionName] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  // Initialize form when division changes (edit mode)
  useEffect(() => {
    if (division) {
      setDivisionName(division.name || '');
      setDescription(division.description || '');
      setVideoUrl(division.video_url || '');

      // Handle image data from server (image is URL string, image_id is the ID)
      if (division.image) {
        setAttachments([
          {
            id: division.image_id,
            name: 'Division Image',
            size: 0,
            sizeFormatted: '',
            url: division.image,
            type: 'image/jpeg',
            isImage: true,
          },
        ]);
      } else {
        setAttachments([]);
      }
    } else {
      resetForm();
    }
  }, [division]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (isEditMode) {
            inputRef.current.select();
          }
        }
      }, 100);
    }
  }, [isOpen, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!divisionName.trim()) {
      setError('Division name is required');
      return;
    }

    // Get image_id from attachments (uploaded files have server-generated IDs)
    const imageId =
      attachments.length > 0 && attachments[0].id
        ? parseInt(attachments[0].id)
        : null;

    const divisionData = {
      name: divisionName.trim(),
      description: description.trim() || null,
      video_url: videoUrl.trim() || null,
      image_id: imageId,
    };

    const success = await onSubmit(divisionData);
    if (success) {
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setDivisionName('');
    setDescription('');
    setVideoUrl('');
    setAttachments([]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {isEditMode ? 'Edit Division' : 'Add Division'}
          </ModalTitle>
          <CloseButton type="button" onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label htmlFor="division_name">
                Division Name
                <RequiredBadge>*Required</RequiredBadge>
              </Label>
              <Input
                ref={inputRef}
                id="division_name"
                type="text"
                placeholder="e.g., Pro Stock, Modified, Late Model"
                value={divisionName}
                onChange={(e) => {
                  setDivisionName(e.target.value);
                  setError('');
                }}
                error={!!error}
              />
              {error && <ErrorText>{error}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <Label>
                Division Image
                <OptionalBadge>(optional)</OptionalBadge>
              </Label>
              <AttachmentWrapper>
                <AttachmentUpload
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  maxFiles={1}
                  maxFileSize={5242880}
                />
              </AttachmentWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="division_description">
                Description
                <OptionalBadge>(optional)</OptionalBadge>
              </Label>
              <Textarea
                id="division_description"
                placeholder="Enter division description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="division_video_url">
                Video URL
                <OptionalBadge>(optional)</OptionalBadge>
              </Label>
              <Input
                id="division_video_url"
                type="text"
                placeholder="https://www.example.com/video"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
              <HelpText>
                Add a YouTube or Vimeo link to showcase this division.
              </HelpText>
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <CancelButton
              type="button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </CancelButton>
            <SubmitButton type="submit" disabled={loading}>
              {loading
                ? 'Saving...'
                : isEditMode
                ? 'Save Changes'
                : 'Add Division'}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};

DivisionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  division: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    video_url: PropTypes.string,
    image: PropTypes.string,
    image_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  loading: PropTypes.bool,
};

export default DivisionModal;
